import type { Api_Product } from '@core/api'
import { EventTypeReady } from '@core/customEvent/eventTypeReady'
import { logMessage } from '@core/logging'

import {
  getCart,
  getProductByHandle,
  resizeShopifyImage,
  updateItemInCart,
} from './helpers'
import type { IShopifyCartItem, IShopifyProduct } from './types'

/** Shopify storefront helpers based on https://shopify.dev/api/ajax */

/**
 * Parse Shopify product to FW product
 */
function parseShopifyProduct(
  shopifyProduct: IShopifyProduct,
  fwProduct: Api_Product,
): Api_Product {
  return window._fwn.shopping.productFactory((builder) => {
    const product = builder
      .id(fwProduct.id)
      .productId(fwProduct.product_id)
      .description(shopifyProduct.description)
      .extId(String(shopifyProduct.id))
      .name(shopifyProduct.title)
      .handle(shopifyProduct.handle)

    // Some old Shopify themes do not have window.Shopify object
    if (window?.Shopify?.currency?.active) {
      product.currency(window.Shopify.currency.active)
    }

    // Process existing images for appending size suffix
    fwProduct.product_images.forEach((product_image) =>
      product.image((i) =>
        i
          .extId(product_image.image_ext_id)
          .position(product_image.image_position)
          .url(resizeShopifyImage(product_image.image_src))
          .imageId(product_image.image_id)
          .unitIds(product_image.unit_ids)
          .unitExtIds(product_image.unit_ext_ids ?? []),
      ),
    )

    // Process variants and its images
    shopifyProduct.variants.forEach((shopifyVariant) => {
      product.variant((v) => {
        const variant = v
          .extId(String(shopifyVariant.id))
          .isAvailable(shopifyVariant.available)
          .name(shopifyVariant.title)
          .price(shopifyVariant.price / 100)
          .originalPrice((shopifyVariant.compare_at_price || 0) / 100)
          .sku(shopifyVariant.sku)
          .url(
            `${window.origin}${shopifyProduct.url}?variant=${shopifyVariant.id}`,
          )

        if (shopifyVariant.featured_image) {
          variant.image((i) =>
            i
              .extId(String(shopifyVariant.featured_image.id))
              .position(shopifyVariant.featured_image.position)
              .title(shopifyVariant.featured_image.alt ?? '')
              .url(resizeShopifyImage(shopifyVariant.featured_image.src)),
          )
        }

        shopifyProduct.options.forEach(({ name, position }) => {
          shopifyVariant.options[position - 1] &&
            variant.option({
              name,
              value: shopifyVariant.options[position - 1],
            })
        })

        return variant
      })
    })

    return product
  })
}

/**
 *
 */
export function hydrateProducts() {
  window._fwn.shopping.onProductsLoaded(async ({ products }) => {
    try {
      return await Promise.all(
        products.map(async (fwProduct: Api_Product) => {
          if (!fwProduct.product_handle) {
            return fwProduct
          }
          try {
            const shopifyProduct = await getProductByHandle(
              fwProduct.product_handle,
            )
            return parseShopifyProduct(shopifyProduct, fwProduct)
          } catch {
            return fwProduct
          }
        }),
      )
    } catch (e: unknown) {
      if (e instanceof Error) {
        // Do not throw error to the player. We do not want to show the error
        // modal when receiving shopify product hydration error.
        logMessage(e.message)
      }
      return []
    }
  })
}

/**
 *
 */
export function createCartProduct(item: IShopifyCartItem, currency: string) {
  return window._fwn.shopping.productFactory((builder) =>
    builder
      .currency(currency)
      .description(item.product_description)
      .extId(String(item.product_id))
      .name(item.product_title)
      .variant((variantBuilder) => {
        variantBuilder
          .extId(String(item.variant_id))
          .isAvailable(true)
          .name(item.title)
          .price(item.final_price / 100)
          .sku(item.sku)
          .url(`${window.origin}${item.url}`)

        item.featured_image &&
          item.featured_image.url &&
          variantBuilder.image((i) =>
            i
              .title(item.featured_image.alt)
              .url(resizeShopifyImage(item.featured_image.url)),
          )
        item.options_with_values.forEach(({ name, value }) => {
          variantBuilder.option({ name, value })
        })
      }),
  )
}

/**
 *
 */
export function hydrateCart() {
  window._fwn.shopping.onCartDisplayed(async () => {
    try {
      const cart = await getCart()
      const { currency, items, token } = cart

      const cartId = token?.includes('?key=')
        ? `gid://shopify/Cart/${token}`
        : undefined

      window._fwn.shopping.configureCart({
        currency,
        cartId,
      })

      return items.map((item) => {
        const product = createCartProduct(item, currency)
        return {
          product,
          unitId: String(item.variant_id),
          quantity: item.quantity,
        }
      })
    } catch (e: unknown) {
      if (e instanceof Error) {
        // Do not throw error to the player. We do not want to show the error
        // modal when receiving shopify cart hydration error.
        logMessage(e.message)
      }
      return []
    }
  })
}

/**
 *
 */
export function cartUpdated() {
  window._fwn.shopping.onCartUpdated(async ({ productUnit, quantity }) => {
    try {
      await updateItemInCart(Number(productUnit.unit_ext_id), quantity)
      return quantity
    } catch (e) {
      if (e instanceof Error) {
        // We do not want to show the original shopify error because they are
        // not under our control. We will show a generic error message instead.
        logMessage(e.message)
      }
      throw new Error('Please try again later.')
    }
  })
}

/**
 *
 */
export function requestCheckout() {
  document.addEventListener('fw:shopping:request-checkout', async () => {
    // Request checkout
  })
}

function setupCallbacks() {
  window._fwn.shopping.configureCart({
    quickAdd: true,
  })
  hydrateProducts()
  hydrateCart()
  cartUpdated()
  requestCheckout()
}

/**
 *
 */
export function setup() {
  window._fwnStoreFront = {
    settings: { name: 'Shopify', cartUrl: location.origin + '/cart' },
  }

  if (window._fwn?.shopping) {
    setupCallbacks()
  } else {
    document.addEventListener(EventTypeReady.FW_SHOPPING_READY, () => {
      setupCallbacks()
    })
  }
}
